import { useEffect, useRef, useState } from "react";
import {
  ALL_VARIABLES,
  COUNTER_VAR,
  OCRFILENAME_VAR,
  SOURCEFILENAME_VAR,
  UUID_VAR,
} from "../../models/transformation/TransformRule";
import { calculateInputLeft, useTooltip } from "../hooks/useTooltip";
import { InputGroup } from "../inputGroup/InputGroup";
import { Label } from "../inputGroup/Label";
import { AdvancedTooltip } from "../tooltips/AdvancedTooltip";
import { TTReplaceWithLabel } from "../tooltips/ConfigurationTooltips";

export { ReplaceTextWithSuggestions, tableVariables as TABLE_VARIABLES };

const defaultVariables = [SOURCEFILENAME_VAR, OCRFILENAME_VAR, UUID_VAR];
const tableVariables = defaultVariables.concat(COUNTER_VAR);

type Props = {
  replaceText: string;
  /**
   * When the user enters `$$` in the text input this array is used to suggest DLF Variable Transformations
   */
  fields: string[];
  /**
   * Variables here should have %% prefix
   */
  variables?: string[];
  update: (a: string) => void;
};

const PARTIAL_DLF_SUGGESTION = /\$\$[\w\d\s]*#{0,1}($|\s)/;
const PARTIAL_VARIABLE_SUGGESTION = /\w+%%($|\s)/;

const ReplaceTextWithSuggestions = (props: Props) => {
  const suggestionClickedRef = useRef<boolean>(false);

  const uuidRef = useRef(Math.random() + "" + Math.random()).current;
  // triggerRef because react-laang doesn't update when our buttons rerendered
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  // container to attach suggestion tooltips too as tooltip cannot be attached to <input>
  const groupRef = useRef<HTMLSpanElement | null>(null);
  // calculate size and position of tooltip
  const inputRef = useRef<HTMLInputElement | null>(null);

  const [replaceText, setReplaceText] = useState<string>(props.replaceText);

  const [suggestionType, setSuggestionType] = useState<
    "var" | "dlf" | undefined
  >();

  useEffect(() => {
    if (replaceText !== props.replaceText) {
      setReplaceText(props.replaceText);
    }
  }, [props.replaceText]);

  const { isOpen, layerProps, renderLayer, setOpen, triggerProps } = useTooltip(
    groupRef,
    undefined,
    undefined,
    undefined,
    "bottom-start",
    ["bottom-start"],
  );

  /**
   * Open correct suggestions dropdown when partial DLF var regex matches or %% cannot be removed
   */
  useEffect(() => {
    if (replaceText.match(PARTIAL_DLF_SUGGESTION) != null) {
      setSuggestionType("dlf");
      setOpen(true);
    } else if (replaceText.includes("%%")) {
      const varsRemoved = ALL_VARIABLES.reduce(
        (prev, curr) => prev.replace(curr, ""),
        replaceText,
      );
      if (varsRemoved.includes("%%")) {
        setSuggestionType("var");
        setOpen(true);
      }
    } else {
      setSuggestionType(undefined);
      setOpen(false);
    }
  }, [replaceText]);

  // useEffect(() => {
  //     if(suggestionType) {
  //     }
  // }, [suggestionType])

  const hasDlfSuggestions = props.fields && props.fields.length;

  // update react-laang trigger ref as needed
  useEffect(() => {
    triggerProps?.ref(triggerRef.current);
  }, [triggerRef, triggerRef.current]);

  return (
    <InputGroup ref={groupRef} className={"replaceTextIg"}>
      <AdvancedTooltip tooltipText={TTReplaceWithLabel}>
        {(tProps) => <Label {...tProps}>With</Label>}
      </AdvancedTooltip>
      <input
        name={uuidRef}
        ref={inputRef}
        type={"text"}
        value={replaceText}
        onChange={(e) => {
          suggestionClickedRef.current = false;
          setReplaceText(e.target.value);
        }}
        onBlur={(e) => {
          console.log(
            "onBlur input",
            replaceText,
            props.replaceText === replaceText,
            e.relatedTarget,
            suggestionClickedRef.current,
          );
          if (props.replaceText === replaceText) return e;
          if (suggestionClickedRef.current) {
            console.log(
              `suggestionClickedRef.current is true so not updating onBlur`,
            );
            return;
          }
          props.update(replaceText);
        }}
      />
      {isOpen &&
        renderLayer(
          <div
            {...layerProps}
            className={"suggestionWrapper"}
            // merge layerProps.style
            style={{
              ...layerProps.style,
              display: !hasDlfSuggestions ? "none" : undefined,
              width: inputRef.current!.clientWidth - 2,
              left: calculateInputLeft(inputRef.current!),
              height: "auto",
            }}
          >
            {suggestionType === "dlf" &&
              hasDlfSuggestions &&
              props.fields.map((x, i) => (
                <span
                  key={x + i}
                  className={"suggestion monospace"}
                  onMouseDown={() => (suggestionClickedRef.current = true)}
                  onClick={() => {
                    // get last partial dlf variable before cursor
                    const matches = replaceText.match(PARTIAL_DLF_SUGGESTION);
                    if (matches && matches.index != undefined) {
                      // get text before $$text#
                      const beforeMatchedSub = replaceText.substring(
                        0,
                        matches.index,
                      );
                      // insert variable, remove $$text#, add text following space from $$text#
                      const merged =
                        beforeMatchedSub +
                        "$$" +
                        x +
                        "##" +
                        inputRef.current!.value.slice(
                          matches.index + matches[0].length,
                        );
                      setReplaceText(merged);
                      props.update(merged);
                    }
                    setOpen(false);
                    setSuggestionType(undefined);
                  }}
                >
                  {" "}
                  {"$$" + x + "##"}
                </span>
              ))}
            {suggestionType === "var" &&
              (props.variables ?? defaultVariables).map((x, i) => (
                <span
                  key={x + i}
                  className={"suggestion monospace"}
                  onMouseDown={() => (suggestionClickedRef.current = true)}
                  onClick={() => {
                    if (replaceText.endsWith("%")) {
                      setReplaceText(replaceText + x.replace("%%", ""));
                      props.update(replaceText + x.replace("%%", ""));
                      setOpen(false);
                      setSuggestionType(undefined);
                    }
                  }}
                >
                  {" "}
                  {x}
                </span>
              ))}
          </div>,
        )}
    </InputGroup>
  );
};
